import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { getAttribution } from "@/lib/attribution";
import { toast } from "sonner";
import { Loader2, Calendar, Clock, CheckCircle2, FileDown, X, Phone, ShieldCheck } from "lucide-react";

const iso = (d) => d.toISOString().slice(0, 10);
function next14Days() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 14; i++) {
    const d = new Date(now); d.setDate(now.getDate() + i);
    if (d.getDay() === 0) continue; // Sunday closed
    out.push(d);
  }
  return out;
}

/**
 * ProductCheckout — one component handles all three paid products.
 * Props: open, onClose, productKey, defaultProfile ({name, mobile, email, courseInterest}), leadId
 */
export default function ProductCheckout({ open, onClose, productKey, defaultProfile = {}, leadId }) {
  const [product, setProduct] = useState(null);
  const [profile, setProfile] = useState({
    name: defaultProfile.name || "",
    mobile: defaultProfile.mobile || "",
    email: defaultProfile.email || "",
    courseInterest: defaultProfile.courseInterest || "",
  });
  const [selectedDate, setSelectedDate] = useState("");
  const [slotData, setSlotData] = useState(null);
  const [chosenSlot, setChosenSlot] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("form"); // form | pay | success | pending
  const [status, setStatus] = useState(null);

  const days = next14Days();

  useEffect(() => {
    if (!open) return;
    setStep("form"); setOrder(null); setStatus(null); setChosenSlot("");
    api.get("/products").then((r) => {
      const p = (r.data.products || []).find((x) => x.key === productKey);
      setProduct(p);
      if (p?.requiresSlot && days[0]) {
        const first = iso(days[0]);
        setSelectedDate(first);
      }
    });
    track("product_view", { product_key: productKey });
    // Spec-compliant conversion event for the ₹499 Expert Admission Call CTA.
    // Fires once per checkout-open (which is 1:1 with the CTA click that opened it,
    // whether from AICounseling result page, homepage, pricing, or college pages).
    // page_path + UTM/source params are auto-added by commonParams().
    if (productKey === "EXPERT_CALL_499") {
      track("expert_call_clicked", {
        product_key: productKey,
        source: "product_checkout",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productKey]);

  useEffect(() => {
    if (!product?.requiresSlot || !selectedDate) return;
    api.get(`/slots?date=${selectedDate}`).then((r) => setSlotData(r.data)).catch(() => setSlotData({ slots: [] }));
  }, [selectedDate, product]);

  const canSubmit = () => {
    if (!profile.name.trim()) return false;
    if (!/^\d{10}$/.test(profile.mobile.trim())) return false;
    if (product?.requiresSlot && !chosenSlot) return false;
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) { toast.error("Please complete all required fields."); return; }
    setLoading(true);
    track("product_selected", { product_key: productKey, value: product?.price, currency: "INR" });
    track("begin_checkout", { plan_code: productKey, value: product?.price, currency: "INR",
      items: [{ item_id: productKey, item_name: product?.name, price: product?.price, quantity: 1 }] });
    try {
      const { data } = await api.post("/payments/counselling/order", {
        plan_code: productKey,
        name: profile.name.trim(),
        phone: profile.mobile.trim(),
        email: profile.email.trim() || undefined,
        leadId: leadId || undefined,
        slot: product?.requiresSlot ? chosenSlot : undefined,
        attribution: getAttribution(),
      });
      setOrder(data);
      setStep("pay");
      track("add_payment_info", { plan_code: productKey, value: data.amount, currency: "INR" });
    } catch (e) {
      track("payment_failed", { plan_code: productKey, stage: "order" });
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // Mock/dev confirm — production uses Cashfree hosted payment page (redirect from paymentSessionId).
  const confirmMock = async () => {
    if (!order) return;
    setLoading(true);
    try {
      await api.post("/payments/counselling/confirm", { orderId: order.orderId, phone: profile.mobile.trim() });
      // Poll status until fulfilment is Delivered (or 5s).
      let deadline = Date.now() + 5000;
      let s = null;
      while (Date.now() < deadline) {
        s = await api.get(`/payments/counselling/order/${order.orderId}/status`).then((r) => r.data);
        if (s.paymentStatus === "SUCCESS") break;
        await new Promise((r) => setTimeout(r, 400));
      }
      setStatus(s);
      track("purchase", { transaction_id: order.orderId, value: order.amount, currency: "INR",
        items: [{ item_id: productKey, item_name: order.planLabel, price: order.amount, quantity: 1 }] });
      if (product?.requiresSlot) track("booking_confirmed", { order_id: order.orderId, slot: order.slot });
      if (s?.reportUrl) track("report_generated", { order_id: order.orderId });
      if (s?.paymentStatus === "SUCCESS" && s?.fulfilmentStatus === "Delivered") track("product_delivered", { order_id: order.orderId });
      setStep(s?.paymentStatus === "SUCCESS" ? "success" : "pending");
    } catch (e) {
      track("payment_failed", { plan_code: productKey, stage: "confirm" });
      toast.error("Payment confirmation failed. Please try again.");
    } finally { setLoading(false); }
  };

  if (!open) return null;
  if (!product) {
    return (
      <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/50">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  const dev = !!order?.mock;

  return (
    <div className="fixed inset-0 z-[65] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="product-checkout">
      <div className="absolute inset-0 bg-slate-900/60" onClick={() => !loading && onClose()} />
      <div className="relative w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-700 z-10" data-testid="checkout-close"><X className="h-5 w-5" /></button>
        <div className="p-6 sm:p-7">
          {/* Product header */}
          <div className="pr-6">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#D4A24C]">₹{product.price} · {product.tagline}</p>
            <h3 className="mt-1 font-display text-xl font-bold text-slate-900">{product.name}</h3>
            <p className="mt-1 text-sm text-slate-500">{product.deliverable}</p>
            <p className="mt-1 text-xs text-slate-400">Delivery: {product.delivery}</p>
          </div>

          {step === "form" && (
            <div className="mt-5 space-y-3" data-testid="checkout-step-form">
              <input data-testid="checkout-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                placeholder="Full Name *" className={INP} />
              <input data-testid="checkout-mobile" value={profile.mobile} inputMode="numeric" maxLength={10}
                onChange={(e) => setProfile({ ...profile, mobile: e.target.value.replace(/\D/g, "") })}
                placeholder="Mobile Number *" className={INP} />
              <input data-testid="checkout-email" value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                placeholder="Email (recommended)" className={INP} />
              <input data-testid="checkout-course" value={profile.courseInterest} onChange={(e) => setProfile({ ...profile, courseInterest: e.target.value })}
                placeholder="Preferred Course (e.g. B.Tech, MBA)" className={INP} />

              {product.requiresSlot && (
                <div className="pt-2" data-testid="checkout-slot-picker">
                  <p className="text-xs font-semibold text-slate-700 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Choose a date (IST, Mon–Sat)</p>
                  <div className="mt-2 flex gap-1.5 overflow-x-auto pb-2">
                    {days.map((d) => {
                      const s = iso(d);
                      const active = s === selectedDate;
                      return (
                        <button key={s} data-testid={`slot-date-${s}`} onClick={() => { setSelectedDate(s); setChosenSlot(""); }}
                          className={`shrink-0 rounded-lg border px-3 py-2 text-xs font-semibold ${active ? "border-[#0B2A6B] bg-[#0B2A6B] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>
                          <span className="block">{d.toLocaleDateString("en-IN", { weekday: "short" })}</span>
                          <span className="block text-[10px] font-normal">{d.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-xs font-semibold text-slate-700 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Available 30-min slots</p>
                  {slotData?.closed ? (
                    <p className="mt-2 text-xs text-slate-400">Sunday — closed. Please choose another day.</p>
                  ) : !slotData ? (
                    <p className="mt-2 text-xs text-slate-400">Loading slots…</p>
                  ) : (
                    <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                      {slotData.slots.map((s) => (
                        <button key={s.slot} disabled={!s.available} data-testid={`slot-${s.slot}`}
                          onClick={() => setChosenSlot(s.slot)}
                          className={`rounded-lg border px-2 py-1.5 text-[11px] font-medium ${chosenSlot === s.slot ? "border-[#D4A24C] bg-[#FDF9F1] text-[#8a6a2b]" : s.available ? "border-slate-200 bg-white text-slate-700 hover:border-slate-300" : "border-slate-100 bg-slate-50 text-slate-300 line-through cursor-not-allowed"}`}>
                          {s.slot.slice(11)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button disabled={!canSubmit() || loading} data-testid="checkout-submit" onClick={submit}
                className="mt-3 w-full rounded-lg bg-[#0B2A6B] py-3 text-sm font-bold text-white disabled:opacity-50 hover:bg-[#081E4D] inline-flex items-center justify-center gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Continue · Pay ₹{product.price}
              </button>
              <p className="flex items-center justify-center gap-1 text-[11px] text-slate-400"><ShieldCheck className="h-3 w-3" /> Secure Cashfree payment · No hidden charges · No admission guarantee</p>
            </div>
          )}

          {step === "pay" && order && (
            <div className="mt-5" data-testid="checkout-step-pay">
              <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-100">
                <div className="flex justify-between text-sm"><span className="text-slate-600">{order.planLabel}</span><span className="font-bold text-slate-900">₹{order.amount}</span></div>
                {order.slot && <p className="mt-1 text-xs text-slate-500">Slot: {order.slot.replace("T", " ")} IST</p>}
              </div>
              {dev && (
                <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-amber-100">
                  ⚠ Payment gateway is in <b>demo mode</b>. In production this will redirect to Cashfree's secure page.
                </div>
              )}
              <button data-testid="checkout-pay" disabled={loading} onClick={confirmMock}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-[#D4A24C] py-3 text-sm font-bold text-white hover:bg-[#C08E3B] disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
                Pay ₹{order.amount}
              </button>
            </div>
          )}

          {step === "success" && status && (
            <div className="mt-5 text-center" data-testid="checkout-success">
              <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
              <h4 className="mt-3 font-display text-lg font-bold text-slate-900">Payment successful!</h4>
              <p className="mt-1 text-sm text-slate-500">Order reference: <span className="font-mono">{status.orderId}</span></p>
              {status.slot && (
                <p className="mt-1 text-sm text-slate-600">Your consultation slot: <span className="font-semibold">{status.slot.replace("T", " ")} IST</span></p>
              )}
              {status.reportUrl ? (
                <a href={`${process.env.REACT_APP_BACKEND_URL}${status.reportUrl}`} target="_blank" rel="noreferrer"
                  data-testid="download-report" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-5 py-2.5 text-sm font-bold text-white">
                  <FileDown className="h-4 w-4" /> Download My PDF Report
                </a>
              ) : (
                <p className="mt-4 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-800">
                  Our admission expert will {status.slot ? "call you at the scheduled slot" : "contact you shortly"}.
                </p>
              )}
              <button onClick={onClose} className="mt-4 w-full rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-700">Done</button>
            </div>
          )}

          {step === "pending" && (
            <div className="mt-5 text-center" data-testid="checkout-pending">
              <h4 className="mt-2 font-display text-lg font-bold text-slate-900">Payment received — fulfilment pending</h4>
              <p className="mt-1 text-sm text-slate-500">Your order is safely recorded (reference: {status?.orderId}). Our team will contact you shortly with the deliverable.</p>
              <button onClick={onClose} className="mt-4 w-full rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-700">Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const INP = "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-[#0B2A6B]";
